import { Component, Input, inject, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { RouteHelperService } from '../../../../core';
import { TranslateService } from '@ngx-translate/core';

export type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'outline' | 'link';
export type ButtonSize = 'small' | 'medium' | 'large' | 'rounded' | 'larger';


@Component({
    selector: 'app-button',
    standalone: true,
    imports: [CommonModule, RouterLink],
    templateUrl: './button.component.html',
    styleUrl: './button.component.css',
    encapsulation: ViewEncapsulation.None
})
export class ButtonComponent {
    private scroller = inject(ViewportScroller);
    private router = inject(Router);
    private routeHelper = inject(RouteHelperService);
    private translate = inject(TranslateService);

    currentLang = this.translate.getCurrentLang() || 'es';

    /** Tipo visual del botón */
    @Input() type: ButtonType = 'primary';

    /** Tamaño del botón */
    @Input() size: ButtonSize = 'medium';

    /** Estado disabled */
    @Input() disabled: boolean = false;

    /** Ruta interna sin prefijo de idioma (ej: 'contacto', 'professionals') - se procesará con RouteHelperService */
    @Input() route?: string;

    /** routerLink directo (para uso legacy o rutas complejas) */
    @Input() routerLink?: string | any[];

    /** ID del elemento al que hacer scroll (sin #) - se ejecuta DESPUÉS de la navegación */
    @Input() scrollTo?: string;

    /** URL externa (para enlaces fuera de la aplicación) */
    @Input() href?: string;

    /** Target para enlaces externos (_blank, _self, etc.) */
    @Input() target?: string = '_blank';

    @Input() fullWidth: boolean = false;

    @Input() label?: string;

    @Output() clicked = new EventEmitter<Event>();

    /**
     * Obtiene la ruta completa con prefijo de idioma
     */
    get computedRouterLink(): string | any[] | undefined {
        // Si se proporciona 'route', procesarlo con RouteHelperService
        if (this.route !== undefined) {
            return this.routeHelper.buildRoute(this.route);
        }
        // Si no, usar routerLink directamente (legacy)
        return this.routerLink;
    }

    // controlador de clics
    handleClick(event: Event): void {
        if (!this.disabled) {
            this.clicked.emit(event);

            // Si hay scrollTo Y hay navegación (route o routerLink), esperar a que termine la navegación
            if (this.scrollTo && (this.route !== undefined || this.routerLink)) {
                // Prevenir el comportamiento por defecto si es necesario
                const targetRoute = this.computedRouterLink;

                if (targetRoute) {
                    event.preventDefault();

                    // Navegar primero
                    this.router.navigate(Array.isArray(targetRoute) ? targetRoute : [targetRoute]).then(() => {
                        // Después de que la navegación termine, hacer scroll
                        setTimeout(() => {
                            this.scroller.scrollToAnchor(this.scrollTo!);
                        }, 100);
                    });
                }
            }
            // Si solo hay scrollTo sin navegación, hacer scroll directamente
            else if (this.scrollTo && !this.route && !this.routerLink) {
                this.scroller.scrollToAnchor(this.scrollTo!);
            }
        }
    }

    /**
     * Genera las clases CSS dinámicas
     */
    get buttonClasses(): string {
        const classes = [
            'app-button',
            `app-button--${this.type}`,
            `app-button--${this.size}`
        ];

        if (this.disabled) classes.push('app-button--disabled');
        if (this.fullWidth) classes.push('app-button--full-width');

        return classes.filter(c => c).join(' ');
    }
}

